import React, { useState, useEffect } from 'react';
import Countdown from 'react-countdown';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';
import currencyFormat from './lib/currencyFormat';
import { luhnCheck } from './lib/luhnCheck';
import { IMaskInput } from 'react-imask';
import CancelModal from './components/CancelModal';
import styles from './app.module.css';
import { createPayment, getPseudoOrder } from './service/index';
import { setPartnerChannel } from './service/http';
import Roller from './components/Loader/Roller/Roller';
import { decodeBase64 } from './lib/decodeBase64';
import { toPaymentError } from './lib/extractApiError';
import Checkout from './components/Checkout/Checkout';
import ErrorModal from './components/ErrorModal';

const steps = ['Ödeme Sayfası', 'İşlem Tamamlandı', 'İşlem Tamamlanamadı', 'Geçersiz Link'];

const PAYMENT_ERROR_FALLBACK =
  'Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin, sorun devam ederse kuryeniz ile iletişime geçin.';

const isSamsungChannel = (channel) =>
  typeof channel === 'string' && channel.toUpperCase().startsWith('SAMSUNG');

const isGetmobilChannel = (channel) =>
  typeof channel === 'string' && channel.toUpperCase() === 'GETMOBIL';

const partnerLabel = (channel) => {
  if (isSamsungChannel(channel)) return 'Samsung';
  if (isGetmobilChannel(channel)) return 'Getmobil';
  return 'Hepsiburada';
};

const App = () => {
  let { paymentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const success = searchParams.get('s');
  const error = searchParams.get('e');
  const convertToCargo = searchParams.get('convert_to_cargo') === 'true';

  const [splashLoading, setSplashLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(success ? 1 : 0);
  const [targetDate, setTargetDate] = useState(Date.now() + 10000000);
  const [expanded, setExpanded] = useState(false);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [paymentButtonLoading, setPaymentButtonLoading] = useState(false);
  const [htmlContent, setHtmlContent] = useState(null);
  // { message: string, detail?: string | null }
  const [paymentError, setPaymentError] = useState(null);

  const [card, setCard] = useState({
    cardHolderName: '',
    cardNumber: '',
    expiry: '',
    cvc: '',
  });
  const [expiryError, setExpiryError] = useState(false);
  const [cardNumberError, setCardNumberError] = useState(false);
  const [cvcError, setCvcError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  useEffect(() => {
    let cardNumber = card.cardNumber.replaceAll('-', '');
    if (
      cardNumber.length === 15 &&
      (cardNumber.substring(0, 2) === '34' || cardNumber.substring(0, 2) === '37')
    ) {
      setCardNumberError(!luhnCheck(cardNumber));
    } else if (cardNumber.length === 16) {
      setCardNumberError(!luhnCheck(cardNumber));
    }
  }, [card.cardNumber]);

  useEffect(() => {
    if (
      card.cvc.length > 0 &&
      card.cvc.length < 4 &&
      (card.cardNumber.substring(0, 2) === '34' || card.cardNumber.substring(0, 2) === '37')
    ) {
      setCvcError(true);
    } else if (card.cvc.length > 0 && card.cvc.length < 3) {
      setCvcError(true);
    } else {
      setCvcError(false);
    }
  }, [card.cvc]);
  useEffect(() => {
    if (card.cardHolderName) {
      card.cardHolderName.length < 4 ? setNameError(true) : setNameError(false);
    }
  }, [card.cardHolderName]);
  useEffect(() => {
    if (card.expiry) {
      if (
        card.expiry.length === 5 &&
        !moment().add(-1, 'M').isBefore(moment(card.expiry, 'MM/YY'))
      ) {
        setExpiryError(true);
      } else {
        setExpiryError(false);
      }
    }
  }, [card.expiry]);

  const toggleHeight = () => {
    setExpanded(!expanded);
  };

  const getPaymentDetail = async () => {
    const response = await getPseudoOrder(paymentId, convertToCargo);

    if (response.success) {
      setPartnerChannel(response.data.channel);
      setPaymentDetail(response.data);
      if (response.data.status === 'CREATED' || response.data.payoutStatus === 'PAID') {
        setActiveStep(1);
      }
      setTargetDate(new Date(new Date(response.data.createdAt).getTime() + 60000 * 60 * 3));
    } else {
      setActiveStep(3);
    }

    setSplashLoading(false);
  };

  const createPaymentRequest = async () => {
    setPaymentButtonLoading(true);
    setPaymentError(null);

    try {
      const response = await createPayment(
        {
          uuid: paymentId,
          cardHolderName: card.cardHolderName,
          cardNumber: card.cardNumber.replaceAll('-', ''),
          expireYear: card.expiry.split('/')[1],
          expireMonth: card.expiry.split('/')[0],
          cvc: card.cvc,
        },
        convertToCargo
      );

      if (!response.success || !response.data?.htmlContent) {
        setPaymentError(toPaymentError(response.error, PAYMENT_ERROR_FALLBACK));
        return;
      }

      setHtmlContent(decodeBase64(response.data.htmlContent.replaceAll(' ', '+')));
    } catch (err) {
      console.log(err);
      setPaymentError({ message: PAYMENT_ERROR_FALLBACK });
    } finally {
      setPaymentButtonLoading(false);
    }
  };

  const handleCheckoutError = () => {
    setHtmlContent(null);
    setPaymentError({ message: PAYMENT_ERROR_FALLBACK });
  };

  useEffect(() => {
    getPaymentDetail();
  }, []);

  useEffect(() => {
    if (error) {
      setPaymentError(toPaymentError({ code: null, detail: error }, PAYMENT_ERROR_FALLBACK));
      setSearchParams({});
    }
  }, [error]);

  return (
    <>
      {splashLoading ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ width: '100vw', height: '100vh' }}
        >
          <Roller size={80} />
        </div>
      ) : (
        <>
          <header className={`${styles.header}`}>{steps[activeStep]}</header>

          {htmlContent ? (
            <Checkout html={htmlContent} onError={handleCheckoutError} />
          ) : (
            <>
              {activeStep === 0 && (
                <>
                  <div className={styles.main}>
                    <div className={styles.mainContainer}>
                      <div className={styles.customerName}>Sayın {paymentDetail.sellUser.name}</div>
                      <div className={styles.productInformationContainer}>
                        <div>
                          <img
                            width={60}
                            height={60}
                            src={
                              paymentDetail.order.imageUrl && paymentDetail.order.imageUrl !== ''
                                ? paymentDetail.order.imageUrl
                                : 'https://cdn.getmobil.com/not-found-phone.webp'
                            }
                            alt="product-image"
                          />
                        </div>
                        <div className="d-flex flex-column gap-1">
                          <div className={styles.productHeadText}>Satın Alınacak Cihaz</div>
                          <div>
                            <p className={styles.productInformationText}>
                              {paymentDetail.order.name.split(' ').slice(0, -2).join(' ')}
                            </p>

                            <p className={styles.productInformationText}>
                              {paymentDetail.order.name.split(' ').slice(-2).join(' - ')}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* <div
												className={styles.waiverOfPaymentBtn}
												onClick={() => setCancelModal(true)}
											>
												Ödemeden Vazgeç
											</div> */}
                    </div>
                  </div>
                  <div
                    className={styles.app}
                    style={{ marginBottom: expanded ? '230px' : '100px' }}
                  >
                    <Countdown
                      date={targetDate}
                      onComplete={() => setActiveStep(2)}
                      renderer={({ hours, minutes, seconds, completed }) => {
                        if (completed) {
                          return <span>Geri sayım tamamlandı!</span>;
                        } else {
                          // Geri sayım devam ederken
                          return (
                            <>
                              <div className="d-flex flex-column justify-content-around w-100">
                                <div className="d-flex justify-content-between w-100">
                                  {isSamsungChannel(paymentDetail?.channel) ||
                                  isGetmobilChannel(paymentDetail?.channel) ? (
                                    <div className={styles.partnerName}>
                                      {partnerLabel(paymentDetail?.channel)}
                                    </div>
                                  ) : (
                                    <img src="hepsiburada-logo.png" alt="hepsiburada logo" />
                                  )}
                                  <div>
                                    <span className={styles.countdownProperties}>
                                      {minutes < 10 && '0'}
                                      {minutes}
                                    </span>
                                    <span className={styles.twoDots}>:</span>
                                    <span className={styles.countdownProperties}>
                                      {seconds < 10 && '0'}
                                      {seconds}
                                    </span>
                                  </div>
                                </div>
                                <div className={styles.countdownInfoText}>
                                  {partnerLabel(paymentDetail?.channel)}'dan satın aldığınız cihazı
                                  teslim almak için ödemenizi kalan süre içerisinde tamamlayınız.
                                </div>
                                <div>
                                  <div className="d-flex flex-column mt-2">
                                    <label className={`my-2 ms-1 ${styles.inputLabel}`}>
                                      Kart Numarası
                                    </label>
                                    <IMaskInput
                                      className={styles.input}
                                      type="tel"
                                      placeholder="####-####-####-####"
                                      onAccept={(e) => {
                                        console.log({ e });
                                        setCard({
                                          ...card,
                                          cardNumber: e,
                                        });
                                      }}
                                      mask={
                                        { ...card }.cardNumber.substring(0, 2) === '34' ||
                                        { ...card }.cardNumber.substring(0, 2) === '37'
                                          ? '0000-0000-0000-000'
                                          : '0000-0000-0000-0000'
                                      }
                                      value={card.cardNumber}
                                      error={cardNumberError}
                                    />
                                    {cardNumberError && (
                                      <div className={`${styles.errorText} mt-1`}>
                                        Geçerli bir kart numarası giriniz
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex flex-column mt-2">
                                    <label className={`mb-2 ms-1 ${styles.inputLabel}`}>
                                      Kart Üzerindeki İsim
                                    </label>
                                    <input
                                      className={styles.input}
                                      type="text"
                                      maxLength={35}
                                      placeholder="Kart Sahibinin Adı ve Soyadı"
                                      value={card.cardHolderName}
                                      onChange={(e) => {
                                        const filtered = e.target.value
                                          .replace(/[^A-Za-zÇĞİÖŞÜçğıöşü\s]/g, '')
                                          .replace(/^\s+/, '');
                                        setCard({
                                          ...card,
                                          cardHolderName: filtered,
                                        });
                                      }}
                                    />
                                    {nameError && (
                                      <div className={`${styles.errorText} mt-1`}>
                                        Geçerli bir ad soyad giriniz
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex flex-column mt-2">
                                    <label className={`mb-2 ms-1 ${styles.inputLabel}`}>
                                      Son Kullanma Tarihi
                                    </label>
                                    <IMaskInput
                                      className={styles.input}
                                      type="tel"
                                      placeholder="Ay / Yıl"
                                      onChange={(e) => {
                                        setCard({
                                          ...card,
                                          expiry: e.target.value,
                                        });
                                      }}
                                      mask="00/00"
                                      value={card.expiry}
                                      error={expiryError}
                                    />
                                    {expiryError && (
                                      <div className={`${styles.errorText} mt-1`}>
                                        Geçerli bir tarih giriniz
                                      </div>
                                    )}
                                  </div>
                                  <div className="d-flex flex-column mt-2">
                                    <label className={`mb-2 ms-1 ${styles.inputLabel}`}>
                                      Güvenlik Kodu
                                    </label>
                                    <IMaskInput
                                      className={styles.input}
                                      type="tel"
                                      placeholder="CVC / CVV"
                                      onChange={(e) =>
                                        setCard({
                                          ...card,
                                          cvc: e.target.value,
                                        })
                                      }
                                      mask={
                                        card.cardNumber.substring(0, 2) === '34' ||
                                        card.cardNumber.substring(0, 2) === '37'
                                          ? '0000'
                                          : '000'
                                      }
                                      value={card.cvc}
                                      error={cvcError}
                                    />
                                    {cvcError && (
                                      <div className={`${styles.errorText} mt-1`}>
                                        Geçerli bir güvenlik kodu giriniz
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-center align-items-center my-3">
                                  <img src="poweredGetmobil.png" alt="Powered by Getmobil" />
                                </div>
                                <div className="d-flex justify-content-center align-items-center my-2">
                                  <img height={48} src="footer-images.png" alt="Footer Images" />
                                </div>
                              </div>
                            </>
                          );
                        }
                      }}
                    />
                  </div>
                  {/* Mobil Bottom  */}
                  <div
                    className={`position-fixed w-100 d-flex flex-column bottom-0 ${styles.expandingDiv} `}
                    style={{ height: `${expanded ? '260px' : '132px'}` }}
                    data-testid="bottomSheetBtn"
                  >
                    <div className="d-flex flex-column w-100 justify-content-center align-items-center gap-2">
                      <div
                        className={`d-flex justify-content-between ${styles.expandContainer}`}
                        onClick={toggleHeight}
                        data-testid="clickBtn"
                        style={{
                          padding: '1rem',
                          width: 'calc(100% - 2rem)',
                        }}
                      >
                        <div className="d-flex align-items-center w-100 gap-3">
                          <div className={styles.expandingText}>Ek Tahsilat</div>
                          <div>
                            {expanded ? (
                              <i className="arrow arrow-down mb-1" />
                            ) : (
                              <i className="arrow arrow-up" />
                            )}
                          </div>
                        </div>
                        <div style={{ fontWeight: '700' }}>
                          {currencyFormat(paymentDetail.amount)}
                        </div>
                      </div>
                      <div className="w-100">
                        <div
                          className={`${expanded ? 'd-flex' : 'd-none'} flex-column mb-2 gap-2`}
                          style={{
                            padding: '1rem',
                            width: 'calc(100% - 2rem)',
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <div className="w-50">Cihaz Fiyatı</div>
                            <div style={{ fontWeight: '500' }}>
                              {currencyFormat(paymentDetail.order.orderLinePrice)}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="w-50">
                              {isGetmobilChannel(paymentDetail?.channel)
                                ? 'Takas İndirimi'
                                : 'Takas Teşviği'}
                            </div>
                            <div style={{ fontWeight: '500' }}>
                              {currencyFormat(paymentDetail?.encouragement)}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <div className="w-50">Ödenen Tutar</div>
                            <div style={{ fontWeight: '500' }}>
                              -{currencyFormat(paymentDetail.order.orderLinePaidPrice)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        disabled={
                          paymentButtonLoading ||
                          expiryError ||
                          cardNumberError ||
                          cvcError ||
                          nameError ||
                          card.cardNumber.length < 19 ||
                          card.cvc.length < 3 ||
                          card.expiry.length < 5 ||
                          card.cardHolderName.length < 2
                        }
                        className={`col-9 ${styles.paymentButton} mb-4`}
                        onClick={() => createPaymentRequest()}
                      >
                        Ödemeyi Tamamla
                      </button>
                    </div>
                  </div>
                </>
              )}

              {activeStep === 1 && (
                <>
                  <div className={styles.processCompletedText}>TEBRİKLER</div>
                  <div className={styles.processCompletedContainer}>
                    <img width={96} src="done-icon.png" alt="Done Icon" />
                    <div className={styles.processCompletedContainerText}>
                      Satın alma işleminiz başarıyla tamamlandı. Cihazınızı teslim alabilirsiniz.
                    </div>
                    <div className={styles.processCompletedContainerTextId}>
                      Sipariş Numarası : {paymentDetail?.order?.orderNumber}
                    </div>
                    <hr className="w-100" />
                    <div className={styles.totalCollection}>
                      <div>Toplam Tahsilat:</div>
                      <div>{currencyFormat(paymentDetail?.amount)}</div>
                    </div>
                  </div>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <div className={styles.processCompletedText}>LİNK İPTAL EDİLDİ</div>
                  <div className={styles.processNotCompletedContainer}>
                    <img width={96} src="cancel-icon.png" alt="Cancel Icon" />
                    <div className={styles.processNotCompletedContainerText}>
                      Yeni bir link için kuryeniz ile iletişime geçiniz
                    </div>
                  </div>
                </>
              )}

              {activeStep === 3 && (
                <>
                  <div className={styles.processCompletedText}>ÖDEME LİNKİ GEÇERSİZDİR</div>
                  <div className={styles.processNotCompletedContainer}>
                    <img width={96} src="cancel-icon.png" alt="Cancel Icon" />
                    <div className={styles.invalidLinkText}>
                      Bir problem olduğunu düşünüyorsanız müşteri hizmetleri ile iletişime
                      geçebilirsiniz.
                    </div>
                    <div className={`${styles.invalidLinkText} `}>0 (850) 303 79 79</div>
                  </div>
                </>
              )}
            </>
          )}

          <CancelModal isOpen={cancelModal} closeModal={() => setCancelModal(false)} />
          <ErrorModal
            isOpen={Boolean(paymentError)}
            message={paymentError?.message}
            detail={paymentError?.detail}
            closeModal={() => setPaymentError(null)}
          />
        </>
      )}
    </>
  );
};

export default App;
